import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import http from '@/services/http';
import type { PendingChange } from '@/utils/syncStatus';
import type { IssueCommandResult } from '@/composables/api/useLocationZonePlayerInspectorApi';

/**
 * The player inspector's Schedule tab: what the player reported it holds, and
 * the two actions that reload it.
 *
 * The response is not a model, so its types live here rather than in the
 * generated `src/ts`. They follow the backend's
 * `GET /api/location_zone_player/{id}/schedule`.
 */

/** One file the player reported. `file_name` has its `#missing#total` suffix removed. */
export interface PlayerScheduleFile {
    file_name: string;
    missing_track_count: number;
    total_track_count: number;
}

export type PlayerScheduleState = 'up_to_date' | 'out_of_date' | 'unknown';

export interface PlayerScheduleRow {
    location_zone_custom_playlist_schedule_id: number;
    location_zone_custom_playlist_id: number;
    /** Null when the list belongs to a master zone this user cannot see. */
    list_name: string | null;
    /** The zone that owns the list: this zone or its master. */
    location_zone_id: number;
    custom_playlist_schedule_type_id: number;
    priority: number | null;
    /** The per-type columns the player reads. `day_ids` is empty unless the schedule repeats. */
    starts: { date: string | null; time: string | null; day_ids: number[] };
    ends: { date: string | null; time: string | null };
    on_player: boolean;
    files: PlayerScheduleFile[];
    /** Weekdays a repeating schedule has no file for. Sunday is 1. */
    missing_day_ids: number[];
}

export interface DailyListReason {
    kind: 'template' | 'template_day' | 'template_day_insert';
    name: string;
    updated_at: string;
}

/** Generation attempt statuses: 1 pending, 2 processing, 3 completed, 4 failed. */
export const PLAYLIST_GENERATION_STATUS = {
    PENDING: 1,
    PROCESSING: 2,
    COMPLETED: 3,
    FAILED: 4,
} as const;

export interface PlayerScheduleRunCommand {
    command: string;
    issued_at: string | null;
    acknowledged_at: string | null;
    delivered_at: string | null;
    completed_at: string | null;
}

/**
 * A reload or a generate the server says is in progress. It is rebuilt from the
 * queue, the command history and the generation attempt on every read, so every
 * browser tab sees the same run.
 */
export interface PlayerScheduleRun {
    kind: 'reload' | 'generate';
    /** Server time. The 15-minute stop counts from here. */
    started_at: string;
    /** A reload's waiting ADDCUSTOM row's time, while it waits. */
    queued_scheduled_time: string | null;
    attempt: { id: number; playlist_generation_status_id: number } | null;
    /** The command history item for this player, once one exists. */
    command: PlayerScheduleRunCommand | null;
}

export interface PlayerScheduleReport {
    report: {
        /** `report` is tabasco's record, `dynamo` the DynamoDB fallback, `none` nothing at all. */
        source: 'report' | 'dynamo' | 'none';
        reported_at: string | null;
    };
    custom: {
        state: PlayerScheduleState;
        /** Change log rows newer than the report, newest first, shaped like the Sync popover's. */
        reasons: PendingChange[];
        /** Changes on a master zone this user cannot see. */
        hidden_reason_count: number;
        schedules: PlayerScheduleRow[];
        other_files: PlayerScheduleFile[];
    };
    daily: {
        state: PlayerScheduleState;
        reasons: DailyListReason[];
        generated_at: string | null;
        first_date: string | null;
        last_date: string | null;
        missing_dates: string[];
        /** Ascending by date. */
        files: Array<{ date: string; file: PlayerScheduleFile }>;
        /** Today's date where the player is, which is the calendar its daily lists follow. */
        today: string;
    };
    run: PlayerScheduleRun | null;
    /** Physical and virtual players in the zone: the players Generate loads. */
    zone_player_count: number;
    can_issue_commands: boolean;
    /** Generate also creates a generation attempt, so it needs that permission as well. */
    can_generate: boolean;
}

/** How often the tab re-asks while a run is in progress. */
export const RUN_POLL_INTERVAL_MS = 4_000;

/** How often the tab re-asks otherwise, while it is on screen, so a run someone else started shows up. */
const IDLE_POLL_INTERVAL_MS = 30_000;

/**
 * Whether the tab should still re-ask quickly for this run. The server stops
 * returning a run 15 minutes after it started, by its own clock, so the
 * viewer's clock never ends one.
 */
export function isRunBeingFollowed(run: PlayerScheduleRun | null | undefined): boolean {
    return Boolean(run) && run?.attempt?.playlist_generation_status_id !== PLAYLIST_GENERATION_STATUS.FAILED;
}

/**
 * How long until the tab re-asks: every few seconds while a run is followed,
 * every 30 seconds otherwise, and not at all while nothing runs and the
 * browser tab is hidden. Coming back to the tab asks again at once.
 */
export function schedulePollIntervalMs(run: PlayerScheduleRun | null | undefined, documentHidden: boolean): number | false {
    if (isRunBeingFollowed(run)) {
        return RUN_POLL_INTERVAL_MS;
    }
    return documentHidden ? false : IDLE_POLL_INTERVAL_MS;
}

function scheduleQueryKey(locationZonePlayerId: ComputedRef<number | null>) {
    return ['location-zone-player-schedule', locationZonePlayerId] as const;
}

/** The player's schedule report, re-asked about every 4 seconds while a run is in progress. */
export function useLocationZonePlayerSchedule(locationZonePlayerId: ComputedRef<number | null>) {
    return useQuery({
        queryKey: scheduleQueryKey(locationZonePlayerId),
        queryFn: () =>
            http
                .get(`/api/location_zone_player/${locationZonePlayerId.value}/schedule`)
                .then((response) => response.data as PlayerScheduleReport),
        enabled: computed(() => locationZonePlayerId.value !== null),
        refetchInterval: (query) =>
            schedulePollIntervalMs(query.state.data?.run, typeof document !== 'undefined' && document.hidden),
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
        retry: 0,
    });
}

/** Issue ADDCUSTOM to this player. The answer is the command issuer's issued/queued/skipped breakdown. */
export function useReloadCustomListsMutation(locationZonePlayerId: ComputedRef<number | null>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            http
                .post(`/api/location_zone_player/${locationZonePlayerId.value}/schedule/reload`)
                .then((response) => response.data as IssueCommandResult),
        // Returned, so the button stays off until the report showing the new run is on screen.
        onSettled: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: scheduleQueryKey(locationZonePlayerId) }),
                queryClient.invalidateQueries({ queryKey: ['location-zone-player-commands'] }),
                queryClient.invalidateQueries({ queryKey: ['location-zone-player-command-history'] }),
            ]),
    });
}

/** Generate new daily lists for the player's zone and load them on every player in it. */
export function useGenerateDailyListsMutation(locationZonePlayerId: ComputedRef<number | null>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            http
                .post(`/api/location_zone_player/${locationZonePlayerId.value}/schedule/generate`)
                .then((response) => response.data as { attempt_id: number }),
        // Returned, so the button stays off until the report showing the new run is on screen.
        onSettled: () => queryClient.invalidateQueries({ queryKey: scheduleQueryKey(locationZonePlayerId) }),
    });
}
